import React, { useEffect, useMemo, useState } from 'react';
import { legacyApi, Customer } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { normalizeText } from '../../../components/table-filters/filterPredicates';
import { useTableFilterState } from '../../../hooks/useTableFilterState';

export const LegacyCustomers: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
    const [formData, setFormData] = useState<Partial<Customer>>({});
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        emirate: 'all',
        credit: 'all'
    });

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const fetchCustomers = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : customers.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        try {
            const response = await legacyApi.customers.listPaged({
                q: debouncedQ || undefined,
                emirate: state.emirate !== 'all' ? state.emirate : undefined,
                credit: state.credit as 'all' | 'with-credit' | 'no-credit',
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setCustomers((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (err) {
            console.error(err);
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
            else setLoadingMore(false);
        }
    };

    const { confirm } = useConfirm();

    useEffect(() => {
        fetchCustomers(true);
    }, [debouncedQ, state.emirate, state.credit]);

    const handleEdit = (customer: Customer) => {
        setEditingCustomer(customer);
        setFormData(customer);
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setEditingCustomer(null);
        setFormData({});
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        const ok = await confirm('Are you sure you want to delete this customer?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            await legacyApi.customers.delete(id);
            toast.success('Customer deleted');
            fetchCustomers(true);
        } catch (err) {
            toast.error('Failed to delete customer');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingCustomer) {
                await legacyApi.customers.update(editingCustomer.customer_id, formData);
            } else {
                await legacyApi.customers.create(formData);
            }
            setIsModalOpen(false);
            fetchCustomers(true);
        } catch (err) {
            console.error(err);
            toast.error('Failed to save customer');
        }
    };

    const columns = [
        { header: "ID", accessor: (c: Customer) => `CUS-${c.customer_id}`, className: 'font-mono whitespace-nowrap w-24' },
        { header: "Name", accessor: (c: Customer) => c.customer_name, className: 'font-medium min-w-[200px] max-w-[300px] truncate' },
        { header: "POC", accessor: (c: Customer) => c.poc_name || '-', className: 'w-40 truncate' },
        { header: "Contact", accessor: (c: Customer) => c.poc_contact || '-', className: 'whitespace-nowrap' },
        { header: "Emirate", accessor: (c: Customer) => c.emirate || '-', className: 'w-28 truncate' },
        { header: "Address", accessor: (c: Customer) => c.address || '-', className: 'min-w-[200px] max-w-[300px] truncate' },
        { header: "Email", accessor: (c: Customer) => c.email_address || '-', className: 'min-w-[150px] max-w-[250px] truncate' },
        { header: "TRN", accessor: (c: Customer) => c.trn || '-', className: 'font-mono w-32 truncate' },
        {
            header: "Credit Limit",
            accessor: (c: Customer) => c.credit_limit ? c.credit_limit.toLocaleString() : '-',
            className: 'text-right font-mono tabular-nums w-32'
        },
        {
            header: "Actions",
            accessor: (c: Customer) => (
                <div className="flex justify-end gap-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => navigate(`/financials?tab=customer-statement&customer_id=${c.customer_id}`)}
                        title="Open Statement"
                    >
                        Statement
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleEdit(c)}
                        title="Edit Customer"
                    >
                        <Edit2 className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleDelete(c.customer_id)}
                        title="Delete Customer"
                    >
                        <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                </div>
            ),
            className: 'text-right'
        }
    ];

    const emirateOptions = useMemo(() => {
        return Array.from(new Set(customers.map((c) => normalizeText(c.emirate)).filter(Boolean))).sort();
    }, [customers]);

    if (!hasLoadedOnce && loading) return <div className="p-8 text-center text-muted-foreground">Loading Customers...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Customers</h1>
                    <p className="text-muted-foreground">Customer database - showing {customers.length} of {totalCount}</p>
                </div>
                <button
                    onClick={handleAdd}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
                >
                    <Plus size={16} />
                    Add Customer
                </button>
            </div>

            <div className="min-h-[400px]">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                    <TableFilterBar
                        searchValue={state.q}
                        onSearchChange={(value) => setFilter('q', value)}
                        searchPlaceholder="Search by customer name, contact, emirate, TRN..."
                        controls={[
                            {
                                key: 'emirate',
                                label: 'Emirate',
                                type: 'select',
                                value: state.emirate,
                                onChange: (value) => setFilter('emirate', value),
                                options: [
                                    { value: 'all', label: 'All Emirates' },
                                    ...emirateOptions.map((option) => ({ value: option, label: option }))
                                ]
                            },
                            {
                                key: 'credit',
                                label: 'Credit',
                                type: 'select',
                                value: state.credit,
                                onChange: (value) => setFilter('credit', value),
                                options: [
                                    { value: 'all', label: 'All Credit Limits' },
                                    { value: 'with-credit', label: 'With Credit Limit' },
                                    { value: 'no-credit', label: 'No Credit Limit' }
                                ]
                            }
                        ]}
                        isDirty={isDirty}
                        onClear={clearAll}
                    />
                </div>
                <>
                    <CompactTable
                        data={customers}
                        columns={columns}
                        keyField="customer_id"
                        loading={loading}
                    />
                    {hasMore && (
                        <div className="flex justify-center py-4">
                            <Button variant="ghost" onClick={() => fetchCustomers(false)} disabled={loadingMore}>
                                {loadingMore ? 'Loading...' : 'Load older customers'}
                            </Button>
                        </div>
                    )}
                </>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-lg shadow-lg w-[920px] max-w-[95vw] max-h-[92vh] m-4 flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-xl font-bold">{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
                                <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Customer Name</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full border rounded p-2"
                                    value={formData.customer_name || ''}
                                    onChange={e => setFormData({ ...formData, customer_name: e.target.value })}
                                />
                            </div>
                                <div>
                                <label className="block text-sm font-medium mb-1">POC Name</label>
                                <input
                                    type="text"
                                    className="w-full border rounded p-2"
                                    value={formData.poc_name || ''}
                                    onChange={e => setFormData({ ...formData, poc_name: e.target.value })}
                                />
                            </div>
                                <div>
                                <label className="block text-sm font-medium mb-1">POC Contact</label>
                                <input
                                    type="text"
                                    className="w-full border rounded p-2"
                                    value={formData.poc_contact || ''}
                                    onChange={e => setFormData({ ...formData, poc_contact: e.target.value })}
                                />
                            </div>
                                <div>
                                <label className="block text-sm font-medium mb-1">Email Address</label>
                                <input
                                    type="email"
                                    className="w-full border rounded p-2"
                                    value={formData.email_address || ''}
                                    onChange={e => setFormData({ ...formData, email_address: e.target.value })}
                                />
                            </div>
                                <div>
                                <label className="block text-sm font-medium mb-1">Office Tel</label>
                                <input
                                    type="text"
                                    className="w-full border rounded p-2"
                                    value={formData.office_tel || ''}
                                    onChange={e => setFormData({ ...formData, office_tel: e.target.value })}
                                />
                            </div>
                                <div>
                                <label className="block text-sm font-medium mb-1">Emirate</label>
                                <input
                                    type="text"
                                    className="w-full border rounded p-2"
                                    value={formData.emirate || ''}
                                    onChange={e => setFormData({ ...formData, emirate: e.target.value })}
                                />
                            </div>
                                <div>
                                <label className="block text-sm font-medium mb-1">TRN</label>
                                <input
                                    type="text"
                                    className="w-full border rounded p-2"
                                    value={formData.trn || ''}
                                    onChange={e => setFormData({ ...formData, trn: e.target.value })}
                                />
                            </div>
                                <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Address</label>
                                <textarea
                                    className="w-full border rounded p-2 min-h-[84px]"
                                    value={formData.address || ''}
                                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                                />
                            </div>
                                <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Payment Terms</label>
                                <input
                                    type="text"
                                    className="w-full border rounded p-2"
                                    value={formData.payment_terms || ''}
                                    onChange={e => setFormData({ ...formData, payment_terms: e.target.value })}
                                />
                            </div>
                                <div className="md:col-span-2 bg-amber-50 p-4 rounded border border-amber-200">
                                <label className="block text-sm font-bold mb-1 text-amber-900">Credit Limit (AED)</label>
                                <input
                                    type="number"
                                    className="w-full border rounded p-2 font-mono font-bold"
                                    value={formData.credit_limit || ''}
                                    onChange={e => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) || 0 })}
                                    placeholder="0.00"
                                />
                                <p className="text-xs text-amber-700 mt-1">
                                    Set to 0 to disable credit limit checks for this customer.
                                </p>
                            </div>
                            </div>
                            <div className="border-t px-6 py-4 flex justify-end gap-2 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Save Customer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
